import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import { getCurrencies, getCrypto, getIndices, getCryptoHistory, getCurrencyHistory } from "../lib/api";
import { SimpleLineChart, COLORS } from "../components/Charts";
import CurrencyConverter from "../components/CurrencyConverter";
import { Search } from "lucide-react";

const TABS = [
  { key: "fx", labelKey: "tab_fx" },
  { key: "crypto", labelKey: "tab_crypto" },
  { key: "idx", labelKey: "tab_idx" },
];

export default function Market() {
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("fx");
  const [currencies, setCurrencies] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [indices, setIndices] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null); // {type, id, label, series, color, prefix}

  useEffect(() => {
    getCurrencies().then((d) => setCurrencies(d.currencies || []));
    getCrypto().then((d) => setCrypto(d.crypto || []));
    getIndices().then((d) => setIndices(d.indices || []));
  }, []);

  const filteredFx = useMemo(
    () => currencies.filter((c) => (c.code + " " + (lang === "pt" ? c.name_pt : c.name_en)).toLowerCase().includes(q.toLowerCase())),
    [currencies, q, lang]
  );
  const filteredCrypto = useMemo(
    () => crypto.filter((c) => (c.name + " " + c.symbol).toLowerCase().includes(q.toLowerCase())),
    [crypto, q]
  );
  const filteredIdx = useMemo(
    () => indices.filter((c) => (c.name + " " + c.symbol).toLowerCase().includes(q.toLowerCase())),
    [indices, q]
  );

  const openFx = async (c) => {
    const d = await getCurrencyHistory("USD", c.code, 30);
    setDetail({
      type: "fx",
      label: `USD / ${c.code}`,
      series: d.series,
      color: COLORS.emerald,
      prefix: "",
      extra: `${c.flag} ${lang === "pt" ? c.name_pt : c.name_en}`,
    });
  };
  const openCrypto = async (c) => {
    const d = await getCryptoHistory(c.id, 30);
    setDetail({
      type: "crypto",
      label: `${c.symbol} / USD`,
      series: d.series,
      color: COLORS.gold,
      prefix: "$",
      extra: c.name,
    });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-market">
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
            {t.common.live}
          </span>
          <h1 className="mt-2 font-display text-4xl lg:text-5xl tracking-tight">{t.market.title}</h1>
          <p className="mt-3 max-w-2xl text-neutral-400">{t.market.sub}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            placeholder={t.common.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-gold-500 transition-colors"
            data-testid="market-search"
          />
        </div>
      </div>

      <div className="mt-8 inline-flex gap-1 bg-white/5 border border-white/10 rounded-md p-1">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            data-testid={`tab-${tb.key}`}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              tab === tb.key ? "bg-gold-500 text-black" : "text-neutral-400 hover:text-white"
            }`}
          >
            {t.market[tb.labelKey]}
          </button>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {tab === "fx" && (
            <div className="surface overflow-hidden" data-testid="fx-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.15em] text-neutral-500">
                    <th className="text-left px-4 py-3">{lang === "pt" ? "Moeda" : "Currency"}</th>
                    <th className="text-right px-4 py-3">USD→</th>
                    <th className="text-right px-4 py-3">→USD</th>
                    <th className="text-right px-4 py-3">{t.common.change}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFx.map((c) => (
                    <tr
                      key={c.code}
                      onClick={() => openFx(c)}
                      className="border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`fx-row-${c.code}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{c.flag}</span>
                          <div>
                            <div className="text-white font-mono">{c.code}</div>
                            <div className="text-xs text-neutral-500">{lang === "pt" ? c.name_pt : c.name_en}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-white">
                        {c.rate_to_usd?.toFixed(c.code === "JPY" ? 2 : 4)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-neutral-400">
                        {c.usd_to_rate?.toFixed(4)}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${c.change_pct >= 0 ? "text-gold-400" : "text-red-400"}`}>
                        {c.change_pct >= 0 ? "+" : ""}
                        {c.change_pct?.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "crypto" && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="crypto-grid">
              {filteredCrypto.map((c) => (
                <div
                  key={c.id}
                  onClick={() => openCrypto(c)}
                  className="surface p-4 cursor-pointer hover:border-white/20 transition-colors"
                  data-testid={`crypto-card-${c.id}`}
                >
                  <div className="flex items-center gap-3">
                    {c.image && <img src={c.image} alt={c.name} className="w-8 h-8 rounded-full" />}
                    <div className="flex-1">
                      <div className="font-mono text-white">{c.symbol}</div>
                      <div className="text-xs text-neutral-500 truncate">{c.name}</div>
                    </div>
                    <div
                      className={`font-mono text-xs ${
                        (c.change_24h_pct ?? 0) >= 0 ? "text-gold-400" : "text-red-400"
                      }`}
                    >
                      {(c.change_24h_pct ?? 0) >= 0 ? "+" : ""}
                      {(c.change_24h_pct ?? 0).toFixed(2)}%
                    </div>
                  </div>
                  <div className="mt-3 font-mono text-2xl text-white">
                    ${Number(c.price_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1 font-mono uppercase tracking-widest">
                    MCap $
                    {Number(c.market_cap_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "idx" && (
            <div className="surface overflow-hidden" data-testid="indices-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.15em] text-neutral-500">
                    <th className="text-left px-4 py-3">{lang === "pt" ? "Índice" : "Index"}</th>
                    <th className="text-left px-4 py-3">{t.common.region}</th>
                    <th className="text-right px-4 py-3">{t.common.price}</th>
                    <th className="text-right px-4 py-3">{t.common.change}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIdx.map((c) => (
                    <tr key={c.symbol} className="border-t border-white/5" data-testid={`idx-row-${c.symbol}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{c.flag}</span>
                          <div>
                            <div className="text-white font-mono">{c.symbol}</div>
                            <div className="text-xs text-neutral-500">{c.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-400">{c.region}</td>
                      <td className="px-4 py-3 text-right font-mono text-white">
                        {Number(c.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${c.change_pct >= 0 ? "text-gold-400" : "text-red-400"}`}>
                        {c.change_pct >= 0 ? "+" : ""}
                        {c.change_pct?.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <CurrencyConverter />
          <div className="surface p-5 mt-4 sticky top-28" data-testid="market-detail">
            {!detail ? (
              <>
                <h3 className="font-display text-lg">
                  {lang === "pt" ? "Clique em um ativo" : "Click an asset"}
                </h3>
                <p className="text-sm text-neutral-500 mt-2">
                  {lang === "pt"
                    ? "Selecione qualquer moeda ou cripto para ver o histórico de 30 dias."
                    : "Pick any currency or crypto to see the 30-day history."}
                </p>
              </>
            ) : (
              <>
                <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
                  {detail.extra}
                </div>
                <h3 className="font-display text-xl mt-1">{detail.label}</h3>
                <div className="mt-3">
                  <SimpleLineChart
                    data={detail.series}
                    color={detail.color}
                    prefix={detail.prefix}
                    height={240}
                    testid="detail-chart"
                  />
                </div>
                <div className="mt-4 border-t border-white/5 pt-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
                    {t.takeaway}
                  </div>
                  <p className="text-sm text-neutral-300 mt-1.5">
                    {buildTakeaway(detail, lang)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function buildTakeaway(d, lang) {
  if (!d || !d.series || d.series.length < 2) return "";
  const first = d.series[0].value;
  const last = d.series[d.series.length - 1].value;
  const pct = ((last / first) - 1) * 100;
  const updown = pct >= 0 ? (lang === "pt" ? "subiu" : "rose") : (lang === "pt" ? "caiu" : "fell");
  if (lang === "pt") {
    return `Nos últimos 30 dias, ${d.label} ${updown} ${Math.abs(pct).toFixed(2)}%. Movimentos recentes podem refletir tanto mudanças macro globais quanto fluxo de curto prazo.`;
  }
  return `Over the last 30 days, ${d.label} ${updown} ${Math.abs(pct).toFixed(2)}%. Recent moves may reflect both global macro shifts and short-term flows.`;
}
